import { Badge, Box, Flex, Heading, HStack, Text, VStack } from "@chakra-ui/react"
import { useMemo } from "react"

import ErrorAlert from "../../components/ErrorAlert"
import SectionCard from "../../framework/SectionCard"
import { useReviewFeedback } from "../../hooks/useReviewFeedback"
import { extractErrorInfo } from "../../lib/api/error-utils"
import { formatDateTime } from "../../utils/datetime"

function sentimentColor(sentiment: string | null) {
  if (sentiment === "positive") return "green"
  if (sentiment === "negative") return "red"
  if (sentiment === "neutral") return "yellow"
  return "gray"
}

export default function ReviewFeedbackDashboard() {
  const { data, isLoading, error } = useReviewFeedback(300)

  const errorInfo = useMemo(() => {
    if (!error) return null
    return extractErrorInfo(error)
  }, [error])

  return (
    <Box p="6">
      <Heading size="2xl" mb="6">
        Feedback
      </Heading>

      {errorInfo && (
        <ErrorAlert message={errorInfo.message} traceId={errorInfo.traceId} />
      )}

      <HStack gap="4" align="stretch" mb="6">
        <SectionCard>
          <VStack align="start" gap="1">
            <Text color="gray.400" fontSize="sm">Total Feedback</Text>
            <Text fontSize="3xl" fontWeight="bold">
              {data?.TotalCount ?? 0}
            </Text>
          </VStack>
        </SectionCard>
        <SectionCard>
          <VStack align="start" gap="1">
            <Text color="gray.400" fontSize="sm">Positive</Text>
            <Text fontSize="3xl" fontWeight="bold" color="green.300">
              {data?.TotalPositiveCount ?? 0}
            </Text>
          </VStack>
        </SectionCard>
        <SectionCard>
          <VStack align="start" gap="1">
            <Text color="gray.400" fontSize="sm">Negative</Text>
            <Text fontSize="3xl" fontWeight="bold" color="red.300">
              {data?.TotalNegativeCount ?? 0}
            </Text>
          </VStack>
        </SectionCard>
        <SectionCard>
          <VStack align="start" gap="1">
            <Text color="gray.400" fontSize="sm">Neutral</Text>
            <Text fontSize="3xl" fontWeight="bold" color="yellow.300">
              {data?.TotalNeutralCount ?? 0}
            </Text>
          </VStack>
        </SectionCard>
      </HStack>

      <SectionCard>
        <Flex justify="space-between" align="center" mb="4">
          <Heading size="md">Source Counts</Heading>
        </Flex>
        {(data?.SourceCounts?.length ?? 0) === 0 ? (
          <Text color="gray.400" fontSize="sm">No feedback sources yet.</Text>
        ) : (
          <HStack gap="2" wrap="wrap">
            {data?.SourceCounts?.map((row) => (
              <Badge key={row.Source} colorPalette="blue" py="1" px="2">
                {row.Source}: {row.Count}
              </Badge>
            ))}
          </HStack>
        )}
      </SectionCard>

      <Box mt="6">
        <SectionCard>
        <Flex justify="space-between" align="center" mb="4">
          <Heading size="md">Recent Feedback</Heading>
          <Text fontSize="sm" color="gray.400">
            Showing {data?.RecentFeedback?.length ?? 0} most recent
          </Text>
        </Flex>

        {isLoading ? (
          <Flex align="center" justify="center" py="6">
            <Text fontSize="sm">Loading…</Text>
          </Flex>
        ) : (data?.RecentFeedback?.length ?? 0) === 0 ? (
          <Text color="gray.400" fontSize="sm">No feedback submitted yet.</Text>
        ) : (
          <Box overflowX="auto">
            <Box as="table" width="100%">
              <Box as="thead">
                <Box as="tr">
                  <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Created</Box>
                  <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">User</Box>
                  <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Source</Box>
                  <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Sentiment</Box>
                  <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Message</Box>
                  <Box as="th" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="wide">Device</Box>
                </Box>
              </Box>
              <Box as="tbody">
                {data?.RecentFeedback?.map((row) => (
                  <Box as="tr" key={row.Id}>
                    <Box as="td" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                      <Text fontSize="sm">{formatDateTime(row.CreatedAt)}</Text>
                    </Box>
                    <Box as="td" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                      <VStack align="start" gap="1">
                        <Text fontSize="sm">{row.UserProfileName || "—"}</Text>
                        <Text fontSize="xs" color="gray.400">{row.UserEmail || "—"}</Text>
                        <Text fontSize="xs" color="gray.500">{row.UserUid || "Anonymous / Website"}</Text>
                      </VStack>
                    </Box>
                    <Box as="td" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                      <Badge colorPalette="orange">{row.Source}</Badge>
                    </Box>
                    <Box as="td" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                      <Badge colorPalette={sentimentColor(row.Sentiment)}>
                        {row.Sentiment || "none"}
                      </Badge>
                    </Box>
                    <Box as="td" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                      <VStack align="start" gap="1">
                        <Text fontSize="sm" whiteSpace="pre-wrap">{row.Message || "—"}</Text>
                        {row.Name && <Text fontSize="xs" color="gray.400">Name: {row.Name}</Text>}
                        {row.Email && <Text fontSize="xs" color="gray.400">Email: {row.Email}</Text>}
                      </VStack>
                    </Box>
                    <Box as="td" px="3" py="2" borderBottomWidth="1px" borderColor="gray.700" verticalAlign="top">
                      <VStack align="start" gap="1">
                        <Text fontSize="xs" color="gray.400">Platform: {row.Platform || "—"}</Text>
                        <Text fontSize="xs" color="gray.400">Version: {row.AppVersion || "—"}</Text>
                        <Text fontSize="xs" color="gray.400">Model: {row.DeviceModel || "—"}</Text>
                      </VStack>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
        </SectionCard>
      </Box>
    </Box>
  )
}
